import { useMemo } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";

import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { fetchClientSiteSetup } from "../data/clients-sites";
import { sitesForPicker } from "../lib/setup-site-options";
import { useReadModel } from "../data/useReadModel";
import type { ShellOutletContext } from "../nav/AppShell";
import { PropertyMapEditor } from "../surfaces/PropertyMapEditor";

export function PropertyMapPage() {
  const { client, config, can } = useOutletContext<ShellOutletContext>();
  const [searchParams] = useSearchParams();
  const initialSiteId = searchParams.get("site") ?? undefined;

  const { data: setup } = useReadModel(
    () => fetchClientSiteSetup(client, config.accountId),
    [client, config.accountId],
  );
  const pickerSites = useMemo(
    () => sitesForPicker(setup?.sites ?? [], setup?.clients ?? []),
    [setup],
  );

  // Nested under /setup, so it inherits the setup route's v3 stage chrome: the workbench
  // template owns scrolling here (no subrail - the map is the whole job on this route).
  return (
    <WorkbenchLayout
      className="property-map-page"
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <div>
              <p className="page__crumb">
                <Link to="/setup">Setup</Link>
              </p>
              <h1 className="wb-head__title">Property map</h1>
            </div>
            <p className="wb-head__note">
              {can("schedule.manage")
                ? "Fullscreen escape hatch from Setup. Draw the site boundary, place checkpoint stops, and preview GPS geofence rings on the satellite view. Changes sync to Command Center."
                : "Fullscreen escape hatch from Setup. Property boundaries, checkpoint pins, and geofence rings for this account (read-only)."}
            </p>
          </div>
        </div>
      }
    >
      <PropertyMapEditor
        key={config.accountId}
        client={client}
        accountId={config.accountId}
        canManage={can("schedule.manage")}
        {...(config.mapTilerKey !== undefined ? { mapTilerKey: config.mapTilerKey } : {})}
        pickerSites={pickerSites}
        managedSites={setup?.sites ?? []}
        {...(initialSiteId !== undefined ? { initialSiteId } : {})}
      />
    </WorkbenchLayout>
  );
}
